import {
  addIdTokenLocalStorage, getIdTokenLocalStorage, decodeToken, isJwtValid,
} from '@sbgsports/vault-sso-auth';

// The admin's own token is parked here for the duration of the session. Separate from
// the SSO package's own key so ending a session can hand the original token back.
const ADMIN_TOKEN_STORAGE_KEY = 'catapult:impersonationAdminToken';

interface IActClaim {
  sub?: string;
  email?: string;
}

/**
 * Storage-only half of the impersonation flow, deliberately free of any API imports:
 * the vault-api 401 interceptor has to reach this, and importing the flows in
 * `utils/impersonation` from there would close an import cycle back through
 * `api/vault/api`.
 */

/** The real admin behind the current session, or null when not impersonating. */
export const getImpersonator = (): string | null => {
  const token = getIdTokenLocalStorage();
  if (!token) return null;
  try {
    const { act } = decodeToken(token) as unknown as { act?: IActClaim };
    return act?.email ?? null;
  } catch {
    return null;
  }
};

export const isImpersonating = (): boolean => !!getImpersonator();

export const stashAdminToken = (adminToken: string): void => {
  localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, adminToken);
};

/**
 * Always drops the stash, even when it is not usable: a real SSO token we are declining
 * to restore is one that must not be left sitting in localStorage. Without this, the
 * admin's own token outlives both logout and token expiry.
 */
export const clearImpersonationStash = (): void => {
  localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
};

/** True when the admin's own identity is now the active one. */
export const restoreAdminToken = (): boolean => {
  const adminToken = localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY);

  clearImpersonationStash();

  if (!adminToken || !isJwtValid(adminToken)) return false;

  addIdTokenLocalStorage(adminToken);
  return true;
};

/**
 * Called when the active token has been rejected. An impersonation session that simply
 * timed out can hand the admin back their own session instead of bouncing them through
 * SSO; anything else is a dead session that has to log in again. Either way the stash is
 * cleared, so it cannot be orphaned by a session that ended without the End button.
 *
 * Returns true if the caller should reload, false if it should redirect to login.
 */
export const recoverFromExpiredToken = (): boolean => {
  if (!isImpersonating()) {
    clearImpersonationStash();
    return false;
  }

  return restoreAdminToken();
};
